import { FCC } from "@/types/common";
import { Stack, Box, Modal, Typography, styled, Grid } from "@mui/material";
import { IconClose, IconSave } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, text, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { Tooltip } from "@/components/elements/Tooltip";
import { IconErrorOutline } from "@/assets/icons";
import { DebounceInput } from "@/components/elements/Input/Input";
import { Select, SelectAsync } from "@/components/elements/Select";
import { useGetNationalityList } from "@/api/services/global/master";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useGetPartnerList } from "@/api/services/main/partner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTalentList } from "@/api/services/main/talent";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { TGetTalentListReqDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { START_PAGE } from "@/constants/pagination";
import Divider from "@mui/material/Divider";
import { ModalPagination } from "@/components/elements/Pagination/ModalPagination";
import { ERecruitmentStatus } from "@/types/enum";
import { TFilter } from "@/api/services/main/case/dtos/create-case.dto";
import { CaseMatchTable } from "@/features/case/components/CaseMatches/CaseMatchTable";
import { createCaseMatchesApi } from "@/api/services/main/case";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CASE_EDIT_TALENT_STORAGE_KEY } from "@/features/case/constants/storage-keys";

type TTalentListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  companySpecificIndustryId?: number;
  caseId?: number;
  jobId?: number;
};

const defaultValues: TFilter = {
  keyword: "",
  nationalityId: undefined,
  personInChargeId: undefined,
  partnerId: undefined,
};

export const TalentListModal: FCC<TTalentListModalProps> = ({ isOpen, onClose, companySpecificIndustryId, caseId, jobId }) => {
  const [filter, setFilter] = useState<TFilter>({});
  const [formValues, setFormValues] = useState<Partial<TFilter>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState<number>(START_PAGE);
  const [localSort, setLocalSort] = useState<string>("");
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const queryClient = useQueryClient();

  const { nationalityOptions } = useGetNationalityList();
  const { userOptions } = useGetUserListPersonInCharge();
  const {
    options: partnerOptionsRaw,
    fetchNextPage: partnerFetchNextPage,
    hasNextPage: partnerHasNextPage,
    isFetching: partnerFetching,
    isFetchingNextPage: partnerFetchingNext,
  } = useGetPartnerList("");
  const partnerOptions = useMemo(
    () => partnerOptionsRaw.map((opt) => ({ label: opt.label, value: Number(opt.value) })),
    [partnerOptionsRaw],
  );

  const { companySpecificIndustryId: _fvIndustry, nationalityId: fvNationality, personInChargeId: fvPerson, ...restFormValues } = formValues;
  const query: TGetTalentListReqDto = {
    page: page,
    perPage: 10,
    recruitmentStatus: [ERecruitmentStatus.JOB_SEEKING],
    isCase: 1,
    companySpecificIndustryId: companySpecificIndustryId ? [companySpecificIndustryId] : undefined,
    jobId,
    ...(caseId && { caseId }),
    ...restFormValues,
    ...(fvNationality ? { nationalityId: [fvNationality] } : {}),
    ...(fvPerson ? { personInChargeId: [fvPerson] } : {}),
    ...(localSort && { sorts: [localSort as `${string}|${string}`] }),
  };

  const { data: talentResponse, isLoading, isFetching } = useTalentList(query, isOpen);

  const onSearch = useCallback((value: TFilter) => {
    setFormValues(value);
    setPage(START_PAGE);
  }, []);

  const clearFilter = () => {
    setFilter({ ...defaultValues });
    onSearch(defaultValues);
  };

  const handleChange = (key: keyof TFilter, value: number | string | undefined) => {
    setFilter((prev) => {
      const newFilter = { ...prev };
      if (value === undefined || value === null || value === "" || value === 0) {
        delete newFilter[key];
      } else {
        if (key === "keyword" || key === "talentNumber" || key === "fullName" || key === "nickName") {
          newFilter[key] = value as string;
        } else if (key === "nationalityId" || key === "personInChargeId" || key === "partnerId" || key === "companySpecificIndustryId") {
          newFilter[key] = value as number;
        }
      }
      return newFilter;
    });
  };

  useEffect(() => {
    const isFilterEmpty = !filter.keyword && !filter.nationalityId && !filter.personInChargeId && !filter.partnerId;

    const isFormValuesEmpty = !formValues.keyword && !formValues.nationalityId && !formValues.personInChargeId && !formValues.partnerId;

    if (isFilterEmpty && !isFormValuesEmpty) {
      onSearch(defaultValues);
    }
  }, [filter, formValues, onSearch]);

  // Reset filter, page, sort, and sessionStorage when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setPage(START_PAGE);
      sessionStorage.removeItem(CASE_EDIT_TALENT_STORAGE_KEY);
    } else {
      setFilter({ ...defaultValues });
      setFormValues({});
      setPage(START_PAGE);
      setLocalSort("");
    }
  }, [isOpen]);

  const handleLocalSort = useCallback((sortKey: string) => {
    setLocalSort(sortKey);
    setPage(START_PAGE);
  }, []);

  const isSubmitEnabled = useMemo(() => {
    return Boolean(filter.keyword || filter.nationalityId || filter.personInChargeId || filter.partnerId);
  }, [filter]);

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  // Always use page state as source of truth for UI
  // This ensures UI is not affected by query changes or response delays
  const perPage = talentResponse?.pagination?.perPage ?? 10;
  const count = talentResponse?.pagination?.count ?? 10;
  const currentPage = page; // Always use state page for UI display

  const countItem = count + perPage * currentPage - perPage;

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSave = useCallback(async () => {
    if (!caseId) {
      toast.error("案件IDがありません。");
      return;
    }

    // Get selected talents from sessionStorage
    const stringTalents = sessionStorage.getItem(CASE_EDIT_TALENT_STORAGE_KEY) ?? "";
    if (!stringTalents) {
      toast.error("人材を選択してください。");
      return;
    }

    let talentIds: number[] = [];
    try {
      const parsed = JSON.parse(stringTalents);
      const rawTalentIds = Array.isArray(parsed) ? parsed : Object.values(parsed ?? {}).flat();
      // Convert to numbers to ensure they are numbers, not strings
      talentIds = rawTalentIds.map((id) => Number(id)).filter((id) => !isNaN(id));
      if (talentIds.length === 0) {
        toast.error("人材を選択してください。");
        return;
      }
    } catch {
      toast.error("人材を選択してください。");
      return;
    }

    setIsSaving(true);
    try {
      await createCaseMatchesApi(caseId, { talentIds });
      toast.success(API_SUCCESS_MESSAGE);

      // Invalidate queries to refresh both case info and case matches
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_DETAIL, caseId] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_MATCHES, caseId] });

      sessionStorage.removeItem(CASE_EDIT_TALENT_STORAGE_KEY);
      onClose();
    } catch (e) {
      showError(e);
    } finally {
      setIsSaving(false);
    }
  }, [caseId, queryClient, onClose]);

  const hasSelectedTalents = useCallback(() => {
    const stringTalents = sessionStorage.getItem(CASE_EDIT_TALENT_STORAGE_KEY) ?? "";
    if (!stringTalents) return false;
    try {
      const parsed = JSON.parse(stringTalents);
      const rawTalentIds = Array.isArray(parsed) ? parsed : Object.values(parsed ?? {}).flat();
      return rawTalentIds.length > 0;
    } catch {
      return false;
    }
  }, []);

  const handleCloseAttempt = useCallback(() => {
    if (hasSelectedTalents()) {
      setIsConfirmLeaveOpen(true);
    } else {
      onClose();
    }
  }, [hasSelectedTalents, onClose]);

  const handleConfirmLeave = useCallback(() => {
    setIsConfirmLeaveOpen(false);
    sessionStorage.removeItem(CASE_EDIT_TALENT_STORAGE_KEY);
    onClose();
  }, [onClose]);

  const handleCancelLeave = useCallback(() => {
    setIsConfirmLeaveOpen(false);
  }, []);

  return (
    <Modal
      open={isOpen}
      hideBackdrop={false}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        handleCloseAttempt();
      }}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
        "& .MuiBackdrop-root": {
          padding: 0,
        },
      }}
    >
      <ModalContainer>
        <Stack
          direction="row"
          bgcolor={background.disable}
          height="56px"
          minHeight="56px"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
        >
          <Typography variant="body14Light" color={"#0B0C0D"}>
            人材の選択
          </Typography>
          <IconBox onClick={handleCloseAttempt} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 4, pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="cap12Light">キーワード検索</Typography>
                  <Tooltip title="以下から検索ができます。" content="名前、呼び名、パートナー名、職歴、学歴、資格、日本語能力">
                    <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}>
                      <IconErrorOutline width={15} height={15} color={black[80]} />
                    </Box>
                  </Tooltip>
                </Stack>
                <DebounceInput
                  onChange={(e) => handleChange("keyword", e.target.value || undefined)}
                  placeholder="入力してください"
                  value={filter.keyword || ""}
                  size="md"
                  style={{ width: "100%" }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md>
              <Stack spacing={1}>
                <Typography variant="cap12Light">国籍</Typography>

                <Select
                  placeholder="選択してください"
                  value={filter.nationalityId}
                  options={nationalityOptions}
                  onChange={(value) => handleChange("nationalityId", value || undefined)}
                  size="md"
                  style={{ width: "100%" }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md>
              <Stack spacing={1}>
                <Typography variant="cap12Light">パートナー</Typography>
                <SelectAsync
                  nonControl
                  placeholder="選択してください"
                  value={filter.partnerId}
                  onChange={(value) => handleChange("partnerId", value ? Number(value) : undefined)}
                  options={partnerOptions}
                  fetchNextPage={partnerFetchNextPage}
                  hasNextPage={partnerHasNextPage}
                  isFetching={partnerFetching}
                  isFetchingNextPage={partnerFetchingNext}
                  size="md"
                  style={{ width: "100%" }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md>
              <Stack spacing={1}>
                <Typography variant="cap12Light">人材担当者</Typography>
                <Select
                  placeholder="選択してください"
                  value={filter.personInChargeId}
                  onChange={(value) => handleChange("personInChargeId", value || undefined)}
                  options={userOptions}
                  size="md"
                  style={{ width: "100%" }}
                />
              </Stack>
            </Grid>
            <Grid item xs="auto">
              <Stack direction="row" alignItems="end" alignContent="end" spacing={1} mt={3}>
                <Button type="button" variant="text" size="sm" disabled={!isSubmitEnabled}>
                  <Typography variant="body14Semi" onClick={clearFilter}>
                    クリア
                  </Typography>
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => onSearch(filter)} disabled={!isSubmitEnabled}>
                  <Typography variant="body14Semi">検索</Typography>
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  marginTop: "8px",
                  marginBottom: "24px",
                }}
              />
              <Typography variant="sub16Bold" mt={3}>
                人材一覧
              </Typography>
              <Stack direction="row" mt={2} mb={2}>
                <Typography variant="body14Semi" color={text.primary}>
                  全 {formatTotal(talentResponse?.pagination?.total) ?? 0} 件中 {countItem ?? 0} 件を表示
                </Typography>
              </Stack>

              <CaseMatchTable
                rows={talentResponse?.data ?? []}
                page={currentPage}
                isLoading={isLoading || isFetching}
                onLocalSort={handleLocalSort}
                localSort={localSort}
                storageKey={CASE_EDIT_TALENT_STORAGE_KEY}
              />

              <Stack mt={3}>
                <ModalPagination totalPage={talentResponse?.pagination?.totalPage ?? 1} page={currentPage} onChangePage={handlePageChange} />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <hr color={divider.high} />
          <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
            <Button variant="ghost" onClick={handleCloseAttempt} size="sm" disabled={isSaving} sx={{ width: "102px" }}>
              キャンセル
            </Button>
            <Button variant="primary" size="sm" startIcon={<IconSave />} onClick={handleSave} disabled={isSaving} sx={{ width: "88px" }}>
              保存
            </Button>
          </Stack>
        </Box>

        <ConfirmSaveModal open={isConfirmLeaveOpen} onClose={handleCancelLeave} onOk={handleConfirmLeave} enableFooter>
          <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
        </ConfirmSaveModal>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 1400px;
  max-width: calc(100vw - 32px);
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  outline: none;
  position: relative;
  margin: auto;
  &:focus {
    outline: none;
  }
`;
